import { initializeApp, getApps, getApp } from "firebase/app";
import { getFirestore, doc, getDoc, collection, getDocs, query, where, orderBy, limit } from "firebase/firestore";

const firebaseConfigs = {
  staging: {
    projectId: "everforge-fitness-staging",
    appId: "1:48697165878:web:ef990a1b5fea9945bcb097",
    storageBucket: "everforge-fitness-staging.firebasestorage.app",
    apiKey: "AIzaSyB2NeLA_O8tQM2n9CLCCR5So4NLTcd7pTU",
    authDomain: "everforge-fitness-staging.firebaseapp.com",
    messagingSenderId: "48697165878",
  },
  production: {
    projectId: "everforge-fitness-tracker",
    appId: "1:1068713356422:web:7002e5f7b511578de5f507",
    storageBucket: "everforge-fitness-tracker.firebasestorage.app",
    apiKey: "AIzaSyCNpHhxcmwDJD-IpVHr6BoOkJDm1A0nWO4",
    authDomain: "everforge-fitness-tracker.firebaseapp.com",
    messagingSenderId: "1068713356422",
  },
};

function resolveConfig() {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host.includes("staging") || host.includes("localhost") || host === "127.0.0.1") {
      return firebaseConfigs.staging;
    }
    if (
      host === "everforgefitness.com" ||
      host === "www.everforgefitness.com" ||
      host === "everforge.web.app" ||
      host === "everforge-fitness-tracker.web.app"
    ) {
      return firebaseConfigs.production;
    }
  }

  return (import.meta as any).env?.VITE_APP_ENV === "production"
    ? firebaseConfigs.production
    : firebaseConfigs.staging;
}

let dbInstance: any = null;
function getDb() {
  if (dbInstance) return dbInstance;
  try {
    const config = resolveConfig();
    const app = getApps().length > 0 ? getApp() : initializeApp(config);
    dbInstance = getFirestore(app);
    return dbInstance;
  } catch (err) {
    console.warn("[ContentService] Error initializing Firebase:", err);
    return null;
  }
}

export interface PromotionalCampaign {
  enabled: boolean;
  campaignName: string;
  headline: string;
  message: string;
  extraDiscountPercent?: number;
  badgeText?: string;
  bannerTheme?: 'gold' | 'navy' | 'emerald';
}

export interface ClientStory {
  id: string;
  clientName: string;
  headline: string;
  transformationGoal: string;
  durationMonths: number;
  storyQuote: string;
  detailedResults: string;
  featuredOrder: number;
}

/**
 * Fetch active promotional campaign from Firestore (business_settings/campaigns)
 */
export async function fetchActiveCampaign(): Promise<PromotionalCampaign | null> {
  const db = getDb();
  if (!db) return null;
  try {
    const docRef = doc(db, "business_settings", "campaigns");
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data() as PromotionalCampaign;
      return data.enabled ? data : null;
    }
  } catch (err) {
    console.warn("[ContentService] Error fetching promotional campaign:", err);
  }
  return null;
}

/**
 * Fetch top 3 published client stories for homepage directly from Firestore.
 * If Firestore has no documents, returns empty array so deleted stories do not ghost reappearing.
 */
export async function fetchPublishedStories(): Promise<ClientStory[]> {
  const db = getDb();
  if (!db) return [];
  try {
    const storiesRef = collection(db, "clientStories");
    const q = query(
      storiesRef,
      where("status", "==", "published"),
      orderBy("featuredOrder", "asc"),
      limit(3)
    );
    const snap = await getDocs(q);
    const stories: ClientStory[] = [];
    snap.forEach((d) => {
      stories.push({ id: d.id, ...d.data() } as ClientStory);
    });
    return stories;
  } catch (err) {
    console.warn("[ContentService] Error fetching client stories:", err);
    return [];
  }
}
